import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { BookOpen, Plus, X, Trash2, ExternalLink, FileText, CheckCircle2, Clock } from "lucide-react";

const KIND_STYLES = {
  payment:    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  po_receive: "bg-blue-100 text-blue-700 dark:bg-sky-900/30 dark:text-sky-300",
  manual:     "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  reversal:   "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const STATUS_STYLES = {
  posted:            "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  draft:             "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  pending_approval:  "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  reversed:          "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const SOURCE_LABELS = {
  invoice_issued:   "Invoice",
  payment_stripe:   "Payment (Stripe)",
  payment_manual:   "Manual Payment",
  po_receive:       "PO Receipt",
  payroll_accrual:  "Payroll Accrual",
  payroll_payment:  "Payroll Payment",
  reversal:         "Reversal",
};

export default function AdminJournalEntries() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [sourceModal, setSourceModal] = useState(null);

  const load = async () => {
    try {
      const [je, acc] = await Promise.all([
        api.get("/journal-entries?limit=300"),
        api.get("/accounts"),
      ]);
      setRows(je.data || []);
      setAccounts(acc.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.kind === filter);
  const voidEntry = async (r) => {
    if (!confirm("Void this entry? A reversing entry will be created (immutable ledger).")) return;
    try { await api.delete(`/journal-entries/${r.entry_id}`); toast.success("Voided"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Void failed"); }
  };
  const postDraft = async (r) => {
    try { await api.post(`/journal-entries/${r.entry_id}/post`, {}); toast.success("Posted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
  };
  const openSource = async (r) => {
    try {
      const res = await api.get(`/journal-entries/${r.entry_id}/source`);
      setSourceModal({ entry: r, ...res.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "No linked source"); }
  };

  return (
    <div data-testid="admin-journal-entries" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Finance</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
            Journal Entries
          </h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            Every ledger transaction. Auto-posted from payments and PO receives; add manual entries for reclassifications.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="new-je-btn">
          <Plus className="w-4 h-4" /> Manual entry
        </button>
      </div>

      <div className="flex gap-1 border-b border-[#E2E8F0] dark:border-slate-700">
        {["all", "payment", "po_receive", "manual", "reversal"].map((k) => (
          <button key={k} onClick={() => setFilter(k)}
                  data-testid={`je-filter-${k}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider transition ${
                    filter === k
                      ? "border-b-2 border-[#C9A356] text-[#0A2540] dark:text-white font-medium"
                      : "text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
                  }`}>
            {k}{k !== "all" && ` (${rows.filter((r) => r.kind === k).length})`}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="card-pb p-10 text-center text-[#94A3B8] italic">
            No entries {filter !== "all" && `of kind "${filter}"`}.
          </div>
        )}
        {filtered.map((r) => (
          <div key={r.entry_id} className="card-pb p-4" data-testid={`je-row-${r.entry_id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <BookOpen className="w-3.5 h-3.5 text-[#C9A356]" />
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${KIND_STYLES[r.kind] || "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"}`}>
                    {r.kind}
                  </span>
                  {r.status && (
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS_STYLES[r.status] || "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"}`} data-testid={`je-status-${r.entry_id}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  )}
                  <span className="font-mono text-[10px] text-[#94A3B8]">{r.entry_id}</span>
                </div>
                <div className="text-sm text-[#0A2540] dark:text-white mt-1">{r.memo}</div>
                <div className="text-[10px] text-[#94A3B8] flex items-center gap-1 flex-wrap">
                  <span>{new Date(r.entry_date).toLocaleDateString()}</span>
                  {r.source?.type && (
                    <>
                      <span>·</span>
                      <button
                        onClick={() => openSource(r)}
                        className="inline-flex items-center gap-0.5 hover:text-[#0A2540] dark:hover:text-white hover:underline"
                        data-testid={`view-source-${r.entry_id}`}
                      >
                        <FileText className="w-2.5 h-2.5" />
                        {SOURCE_LABELS[r.source.type] || r.source.type}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="tabular-nums font-medium text-[#0A2540] dark:text-white">${r.total_amount?.toLocaleString()}</div>
                <div className="flex items-center gap-2 justify-end mt-1">
                  {r.status === "draft" && (
                    <button onClick={() => postDraft(r)} className="text-[10px] text-emerald-600 hover:underline inline-flex items-center gap-0.5" data-testid={`post-draft-${r.entry_id}`}>
                      <CheckCircle2 className="w-2.5 h-2.5" /> Post
                    </button>
                  )}
                  {r.kind !== "reversal" && !r.voided_by && r.status !== "draft" && r.status !== "pending_approval" && (
                    <button onClick={() => voidEntry(r)} className="text-[10px] text-red-500 hover:underline inline-flex items-center gap-0.5" data-testid={`void-je-${r.entry_id}`}>
                      <Trash2 className="w-2.5 h-2.5" /> Reverse
                    </button>
                  )}
                </div>
              </div>
            </div>
            <table className="w-full text-xs mt-2 border-t border-[#E2E8F0] dark:border-slate-700 pt-2">
              <tbody>
                {r.lines?.map((ln, i) => (
                  <tr key={i}>
                    <td className="py-0.5 font-mono text-[#94A3B8] w-16">{ln.code}</td>
                    <td className="py-0.5 text-[#475569] dark:text-slate-400">{ln.name}</td>
                    <td className="py-0.5 text-right tabular-nums text-[#0A2540] dark:text-white w-24">
                      {ln.debit > 0 ? `$${ln.debit.toLocaleString()}` : ""}
                    </td>
                    <td className="py-0.5 text-right tabular-nums text-[#0A2540] dark:text-white w-24">
                      {ln.credit > 0 ? `$${ln.credit.toLocaleString()}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {creating && <JEModal accounts={accounts} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {sourceModal && <SourceModal data={sourceModal} onClose={() => setSourceModal(null)} />}
    </div>
  );
}

function SourceModal({ data, onClose }) {
  const rec = data.record || {};
  const rel = data.related || {};
  const label = SOURCE_LABELS[data.type] || data.type;

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 dark:border dark:border-slate-700 rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="source-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0] dark:border-slate-700">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Linked source</div>
            <h3 className="font-heading font-semibold text-[#0A2540] dark:text-white">{label}</h3>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0A2540] dark:hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-[#E2E8F0] dark:border-slate-700 p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Business record</div>
            {rec.invoice_number && <Row k="Invoice #" v={rec.invoice_number} />}
            {rec.po_number && <Row k="PO #" v={rec.po_number} />}
            {rec.title && <Row k="Title" v={rec.title} />}
            {rec.amount != null && <Row k="Amount" v={`$${Number(rec.amount).toLocaleString()}`} />}
            {rec.total != null && <Row k="Total" v={`$${Number(rec.total).toLocaleString()}`} />}
            {rec.status && <Row k="Status" v={rec.status} />}
            {rec.week_start && <Row k="Week of" v={rec.week_start} />}
            {rec.timelogs && <Row k="Timelogs" v={`${rec.timelogs.length} entries`} />}
          </div>
          {rel.customer && (
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-[#E2E8F0] dark:border-slate-700 p-3 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Customer</div>
              <Row k="Name"  v={rel.customer.name} />
              <Row k="Code"  v={rel.customer.customer_code} />
              <Row k="Email" v={rel.customer.email} />
            </div>
          )}
          {rel.supplier && (
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-[#E2E8F0] dark:border-slate-700 p-3 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Supplier</div>
              <Row k="Name"  v={rel.supplier.name} />
              <Row k="Email" v={rel.supplier.email} />
              <Row k="Terms" v={rel.supplier.terms} />
            </div>
          )}
          {rel.employee && (
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-[#E2E8F0] dark:border-slate-700 p-3 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Employee</div>
              <Row k="Name"     v={rel.employee.name} />
              <Row k="Code"     v={rel.employee.employee_number || rel.employee.employee_code} />
              <Row k="Email"    v={rel.employee.email} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  if (v == null || v === "") return null;
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11px] text-[#64748B]">{k}</span>
      <span className="text-sm text-[#0A2540] dark:text-white font-medium">{v}</span>
    </div>
  );
}

function JEModal({ accounts, onClose, onSaved }) {
  const [memo, setMemo] = useState("");
  // Stable per-line id lets React track rows across re-renders when the user
  // adds / removes / reorders lines — array-index keys caused inputs to shift
  // and lose focus (flagged by code review).
  const nextId = React.useRef(2);
  const [lines, setLines] = useState([
    { id: "ln-0", code: accounts[0]?.code || "", debit: "", credit: "" },
    { id: "ln-1", code: accounts[1]?.code || "", debit: "", credit: "" },
  ]);
  const [saving, setSaving] = useState(false);

  const upd = (i, patch) => setLines((arr) => arr.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const add = () => setLines((arr) => [...arr, { id: `ln-${nextId.current++}`, code: accounts[0]?.code || "", debit: "", credit: "" }]);
  const rm = (i) => setLines((arr) => arr.filter((_, idx) => idx !== i));

  const debitTotal  = lines.reduce((s, l) => s + (Number(l.debit)  || 0), 0);
  const creditTotal = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = debitTotal === creditTotal && debitTotal > 0;

  const save = async (asDraft = false) => {
    if (!asDraft && !balanced) { toast.error(`Not balanced: D $${debitTotal} vs C $${creditTotal}`); return; }
    if (asDraft && debitTotal === 0 && creditTotal === 0) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      const payload = {
        kind: "manual",
        memo,
        status: asDraft ? "draft" : undefined,
        lines: lines.map((l) => ({
          code: l.code,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        })).filter((l) => l.debit > 0 || l.credit > 0),
      };
      const r = await api.post("/journal-entries", payload);
      const st = r?.data?.status;
      toast.success(st === "pending_approval" ? "Filed for approval" : st === "draft" ? "Draft saved" : "Entry posted");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const cls = "border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1 text-sm";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">Manual journal entry</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Memo</label>
            <input className={`${cls} w-full`} value={memo} onChange={(e) => setMemo(e.target.value)}
                   placeholder="e.g. Reclassify Q1 marketing expense" data-testid="je-memo" />
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Lines</label>
              <button onClick={add} className="text-xs text-[#C9A356] hover:underline" data-testid="je-add-line">+ Add line</button>
            </div>
            <div className="space-y-1.5">
              {lines.map((ln, i) => (
                <div key={ln.id} className="grid grid-cols-[1fr_100px_100px_28px] gap-1 items-center" data-testid={`je-line-${i}`}>
                  <select className={cls} value={ln.code} onChange={(e) => upd(i, { code: e.target.value })} data-testid={`je-line-code-${i}`}>
                    {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                  </select>
                  <input type="number" min="0" step="0.01" className={cls} value={ln.debit}
                         onChange={(e) => upd(i, { debit: e.target.value, credit: "" })}
                         placeholder="Debit" data-testid={`je-line-debit-${i}`} />
                  <input type="number" min="0" step="0.01" className={cls} value={ln.credit}
                         onChange={(e) => upd(i, { credit: e.target.value, debit: "" })}
                         placeholder="Credit" data-testid={`je-line-credit-${i}`} />
                  <button onClick={() => rm(i)} className="text-red-500 hover:text-red-700 disabled:opacity-30"
                          disabled={lines.length <= 2}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className={`text-sm text-right pt-2 border-t border-[#E2E8F0] dark:border-slate-700 ${
            balanced ? "text-emerald-600" : "text-red-500"
          }`} data-testid="je-balance">
            Debits <b className="tabular-nums">${debitTotal.toFixed(2)}</b>
            {" · "}
            Credits <b className="tabular-nums">${creditTotal.toFixed(2)}</b>
            {balanced && <span className="ml-2 text-[10px] uppercase font-bold">✓ Balanced</span>}
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => save(true)} disabled={saving} className="flex-1 px-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-slate-700 text-sm text-[#0A2540] dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800/60 disabled:opacity-50" data-testid="je-save-draft">
              Save as draft
            </button>
            <button onClick={() => save(false)} disabled={saving || !balanced} className="btn-accent text-sm flex-1 disabled:opacity-50" data-testid="je-save">
              {saving ? "Posting…" : "Post entry"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
